import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, User, UserCog, ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

// Los accesos de demostración muestran credenciales reales: solo en desarrollo o si
// el despliegue lo pide explícitamente (REACT_APP_SHOW_DEMO_LOGINS=true).
const SHOW_DEMOS = process.env.NODE_ENV === "development" || process.env.REACT_APP_SHOW_DEMO_LOGINS === "true";

const DEMOS = [
  { key: "admin", label: "Administrador", desc: "Panel completo", email: "info@motivy.co", password: "Armony2025!", icon: ShieldCheck },
  { key: "cliente", label: "Cliente", desc: "Portal · nivel Oro", email: "cliente@armony.co", password: "cliente123", icon: User },
  { key: "staff", label: "Equipo (parcial)", desc: "Canjes y notificaciones push", email: "staff@armony.co", password: "staff123", icon: UserCog },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState("");

  const doLogin = async (mail: string, pass: string) => {
    const u = await login(mail, pass);
    toast.success(`Bienvenido, ${u.name}`);
    navigate(u.role === "customer" ? "/portal" : "/admin");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await doLogin(email, password);
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (d: typeof DEMOS[number]) => {
    setDemoLoading(d.key);
    try {
      await doLogin(d.email, d.password);
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setDemoLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 noise-overlay opacity-50" />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-home-link"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-gold-ink transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center justify-center mb-8">
          <BrandLogo imgClassName="h-8" tag="Contigo" />
        </Link>
        <div className="card-obsidian p-8">
          <h1 className="text-2xl font-serif font-medium text-slate-900 mb-1">Bienvenido de vuelta</h1>
          <p className="text-sm text-slate-500 mb-7">Ingresa a tu portal de fidelización.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-slate-700 text-sm">Correo electrónico</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Contraseña</Label>
              <PasswordInput
                data-testid="login-password-input"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 gold-gradient-bg text-white font-semibold rounded-full hover:brightness-110"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ingresar"}
            </Button>
          </form>

          {SHOW_DEMOS && <div className="mt-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-slate-100" />
              <span className="text-xs uppercase tracking-widest text-slate-500 font-mono">Accesos de demostración</span>
              <div className="h-px flex-1 bg-slate-100" />
            </div>
            <div className="grid gap-2">
              {DEMOS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  data-testid={`demo-login-${d.key}`}
                  onClick={() => quickLogin(d)}
                  disabled={!!demoLoading}
                  className="group flex items-center gap-3 w-full p-3 rounded-xl border border-slate-200 hover:border-gold/50 hover:bg-gold/5 transition-all text-left disabled:opacity-60"
                >
                  <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                    {demoLoading === d.key ? <Loader2 className="w-4 h-4 text-gold animate-spin" /> : <d.icon className="w-4 h-4 text-gold-ink" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900 font-medium">Entrar como {d.label}</p>
                    <p className="text-xs text-slate-500 truncate">{d.email} · {d.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>}

          <p className="text-sm text-slate-500 text-center mt-6">
            ¿No tienes cuenta?{" "}
            <Link to="/registro" data-testid="login-to-register-link" className="text-gold-ink hover:underline">
              Regístrate
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
